import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { api, type LcPoolResponse } from '@/lib/api';

export function CacheEditForm({
  pool,
  onSaved,
  onCancel,
}: {
  pool: LcPoolResponse;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [editName, setEditName] = useState(pool.name);
  const [editDescription, setEditDescription] = useState(pool.description ?? '');
  const [editFeeBps, setEditFeeBps] = useState(String(pool.feeBps));
  const [editMaxTradePct, setEditMaxTradePct] = useState(String(pool.maxTradePct));
  const [editStatus, setEditStatus] = useState(pool.status);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await api.updateLiquidityCachePool(pool.poolId, {
        name: editName !== pool.name ? editName : undefined,
        description: editDescription !== (pool.description ?? '') ? editDescription : undefined,
        feeBps: parseInt(editFeeBps, 10) !== pool.feeBps ? parseInt(editFeeBps, 10) : undefined,
        maxTradePct:
          parseInt(editMaxTradePct, 10) !== pool.maxTradePct
            ? parseInt(editMaxTradePct, 10)
            : undefined,
        status: editStatus !== pool.status ? editStatus : undefined,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update cache');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Edit: {pool.name}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label>Name</Label>
          <Input value={editName} onChange={(e) => setEditName(e.target.value)} />
        </div>
        <div>
          <Label>Description</Label>
          <Input value={editDescription} onChange={(e) => setEditDescription(e.target.value)} />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label>Fee (bps)</Label>
            <Input value={editFeeBps} onChange={(e) => setEditFeeBps(e.target.value)} />
          </div>
          <div>
            <Label>Max Trade %</Label>
            <Input value={editMaxTradePct} onChange={(e) => setEditMaxTradePct(e.target.value)} />
          </div>
          <div>
            <Label>Status</Label>
            <select
              className="w-full rounded-md border px-3 py-2 text-sm"
              value={editStatus}
              onChange={(e) => setEditStatus(e.target.value)}
            >
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>
        <div className="text-xs text-muted-foreground">
          Tokens: {pool.tokenA.name} ({pool.tokenA.symbol}) / {pool.tokenB.name} (
          {pool.tokenB.symbol}) &middot; Min Liquidity: {pool.minLiquidity}
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex gap-2">
          <Button onClick={handleSave} disabled={submitting} size="sm">
            {submitting ? 'Saving...' : 'Save'}
          </Button>
          <Button onClick={onCancel} variant="outline" size="sm">
            Cancel
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
